import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { CircularProgress, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import AppState from "../../store/AppState";
import { selectProductFetched } from "../../store/product/ProductSelectors";
import {
  selectIsModalOpen,
  selectModalVariable,
} from "../../store/modal/ModalSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import {
  componentClasses,
  StyledBox,
  StyledDiv,
  StyledTypography,
} from "./FreqBoughtTogetherModalStyles";
import { VARIATION_GROUP } from "../../utils/Constants";
import CustomDialog from "../common/DialogContainer";
import ButtonComponent from "../common/ButtonComponent";
import { makeStyles, createStyles, Theme } from "@material-ui/core";
import { resetAllProducAnalyticsView } from "store/product/ProductActions";
import FreqBoughtTogetherProductItem from "./FreqBoughtTogetherProductItem";
import {
  fetchFreqBoughtProductsRequestAction,
  resetFreqBoughtList,
} from "store/recommendations/RecommendationAction";
import {
  selectFreqProductsDetails,
  selectIsFreqProductsFetched,
  selectIsProductDetailsFetched,
} from "store/recommendations/RecommendationSelector";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";
import { AdapterInfoObject } from "utils/AppConfigUtils";
import { createCategoriesDisplayedHandler } from "utils/ProductUtil";

export const FreqBoughtTogetherModalId = "FreqBoughtTogetherModal";

interface Props {
  categoryId: string;
}

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    rightSection: {
      position: "sticky",
      top: 0,
      marginLeft: "auto",
      minWidth: "150px",
      width: "auto",
      height: "74vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      alignItems: "flex-end",
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(1),
      [theme.breakpoints.down("md")]: {
        minWidth: "200px",
      },
      overflowY: "auto",
      right: 0,
    },
    divActionBtn: {
      display: "flex",
      gap: 17,
      alignItems: "flex-end",
      justifyContent: "flex-end",
      width: "100%",
      margin: "20px",
    },
    divNewActionBtn: { display: "flex" },
    overlay: {
      position: "fixed",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: 99999,
      cursor: "pointer",
    },
  }),
);
const FreqBoughtTogether: React.FC<Props> = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const classes = useStyle();
  const [isAnalyticsDisplayed, setIsAnalyticsDisplayed] =
    useState<boolean>(false);
  const [categoriesDisplayedProductIds, setCategoriesDisplayedProductIds] =
    useState<Set<string>>(new Set());
  const productId = useSelector((state: AppState) =>
    selectModalVariable(state, FreqBoughtTogetherModalId, "productId"),
  );
  const fetchedProduct = useSelector((state: AppState) =>
    selectProductFetched(state, productId),
  );
  const isFreqProductDetailsFetched = useSelector(
    selectIsProductDetailsFetched,
  );
  const isFetchingFreqProductsIds = useSelector(selectIsFreqProductsFetched);
  const freqProductDetails = useSelector(selectFreqProductsDetails);
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const isProductIdAnalyticIdAPIAvailable = adapterInfo?.operations?.includes(
    "POST /v1/{accountId}/product-analytic-id-query",
  ) as boolean;
  const targetProduct = useMemo(() => {
    if (fetchedProduct?.productId) {
      return {
        productId: fetchedProduct.productId,
        code: fetchedProduct.code,
        analyticsId: fetchedProduct.analyticsId,
        name: fetchedProduct.name,
        thumbnail: fetchedProduct.thumbnail,
        isPublished: fetchedProduct.isPublished,
        storeSpecific:
          fetchedProduct?.storeSpecific &&
          Object.entries(fetchedProduct.storeSpecific).map((e) => {
            return { storeId: e[0], isPublished: e[1].isPublished };
          }),
        translations: fetchedProduct?.translations,
        stock: fetchedProduct.stock,
        baseProductId:
          fetchedProduct?.typeCode === VARIATION_GROUP
            ? fetchedProduct.productId
            : fetchedProduct.variationBaseId,
        price: fetchedProduct.highPrice,
        colors: fetchedProduct.colors,
        categorySpecific: fetchedProduct.categorySpecific,
        hasSequenceableColors: fetchedProduct.hasSequenceableColors,
        typeCode: fetchedProduct.typeCode,
      };
    }
  }, [fetchedProduct]);

  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, FreqBoughtTogetherModalId),
  );

  const handleClose = () => {
    dispatch(setModalState(FreqBoughtTogetherModalId, false));
    dispatch(resetFreqBoughtList());
    dispatch(resetAllProducAnalyticsView());
    setIsAnalyticsDisplayed(false);
    setCategoriesDisplayedProductIds(new Set());
  };

  const handleCategoriesDisplayedChange = createCategoriesDisplayedHandler(
    setCategoriesDisplayedProductIds,
  );

  useEffect(() => {
    if (isModalOpen && targetProduct && targetProduct?.productId) {
      const payload = {
        productId: targetProduct.analyticsId ?? targetProduct.code,
        period: 30,
        limit: 8,
        isProductIdAnalyticIdAPIAvailable,
      };
      dispatch(fetchFreqBoughtProductsRequestAction(payload));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isModalOpen]);

  const rightSection = () => {
    return (
      <>
        <div className={classes.divActionBtn}>
          <ButtonComponent
            color="primary"
            variant="contained"
            onClick={() => handleClose()}
          >
            {intl.formatMessage({
              id: "general.close",
              defaultMessage: "Close",
            })}
          </ButtonComponent>
        </div>
      </>
    );
  };

  const FreqBoughtTogetherModalTitle = () => {
    return (
      <Typography variant="inherit">
        {intl.formatMessage({
          id: "frequentlyBoughtTogether.title",
          defaultMessage: "Frequently Bought Together",
        })}{" "}
        - {fetchedProduct?.name ?? ""} {`(${fetchedProduct?.code})`}
      </Typography>
    );
  };

  const freqBoughtTogetherModalContent = () => {
    return (
      <>
        <StyledDiv className={componentClasses.paper}>
          <StyledDiv className={componentClasses.leftSection}>
            <StyledTypography className={componentClasses.baseProduct}>
              {intl.formatMessage({
                id: "frequentlyBoughtTogether.baseProductTitle",
                defaultMessage: "Product",
              })}
            </StyledTypography>
            {targetProduct && (
              <FreqBoughtTogetherProductItem
                product={targetProduct}
                code={targetProduct?.code}
                productId={targetProduct?.productId}
                isColumn={false}
              />
            )}
          </StyledDiv>
          <StyledDiv>
            <StyledDiv
              className={componentClasses.selectProductToSubstitute}
            ></StyledDiv>
            <StyledDiv className={componentClasses.mainContent}>
              <StyledBox className={componentClasses.mainSection}>
                {isFetchingFreqProductsIds || isFreqProductDetailsFetched ? (
                  <StyledDiv className={componentClasses.progress}>
                    <CircularProgress style={{ margin: "auto" }} size="40px" />
                  </StyledDiv>
                ) : freqProductDetails && freqProductDetails.length > 0 ? (
                  freqProductDetails?.map((product, productIndex) => (
                    <FreqBoughtTogetherProductItem
                      product={product}
                      code={product?.code ?? ""}
                      price={product?.highPrice}
                      productId={product?.productId ?? ""}
                      isColumn={true}
                      index={productIndex}
                      key={productIndex.toString()}
                      isAnalyticsDisplayed={isAnalyticsDisplayed}
                      setIsAnalyticsDisplayed={setIsAnalyticsDisplayed}
                      isCategoriesDisplayed={categoriesDisplayedProductIds.has(
                        product?.productId ?? "",
                      )}
                      setIsCategoriesDisplayed={(value: boolean) =>
                        handleCategoriesDisplayedChange(
                          value,
                          product?.productId,
                        )
                      }
                    />
                  ))
                ) : (
                  <StyledBox className={componentClasses.noSimilarProducts}>
                    <Typography variant="h5" style={{ textAlign: "center" }}>
                      {intl.formatMessage({
                        id: "freqBoughtTogether.noProductsFoundText",
                        defaultMessage:
                          "No frequently bought together products found",
                      })}
                    </Typography>
                  </StyledBox>
                )}
              </StyledBox>
            </StyledDiv>
          </StyledDiv>
        </StyledDiv>
      </>
    );
  };

  return (
    <CustomDialog
      open={isModalOpen}
      onClose={() => handleClose()}
      title={FreqBoughtTogetherModalTitle()}
      fullWidth
      maxWidth={"lg"}
      onClick={(e) => e.preventDefault()}
      actions={rightSection()}
    >
      {freqBoughtTogetherModalContent()}
    </CustomDialog>
  );
};

export default React.memo(FreqBoughtTogether);
